import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Calendar as CalendarIcon,
  List,
  User,
  Clock,
  MapPin,
  Phone,
  ChevronLeft,
  ChevronRight,
  Loader2,
  FileText,
  AlertCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  received: 'bg-blue-100 text-blue-800',
  confirmed: 'bg-green-100 text-green-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  completed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

const priorityColors = {
  urgent: 'bg-red-500 text-white',
  high: 'bg-orange-500 text-white',
  normal: 'bg-blue-500 text-white',
  low: 'bg-gray-500 text-white'
};

export const SpecialistCalendarPage = () => {
  const { t } = useTranslation('specialists');
  const [specialists, setSpecialists] = useState([]);
  const [selectedSpecialist, setSelectedSpecialist] = useState('');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  useEffect(() => {
    fetchSpecialists();
  }, []);

  useEffect(() => {
    if (selectedSpecialist) {
      fetchOrders();
    }
  }, [selectedSpecialist, currentDate]);

  const fetchSpecialists = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/specialists?limit=100`);
      // API returns an array directly
      const data = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setSpecialists(data);
    } catch (error) {
      console.error('Error fetching specialists:', error);
      toast.error(t('calendar.messages.loadSpecialistsError'));
    }
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/orders`, {
        params: {
          specialist_id: selectedSpecialist,
          limit: 100
        }
      });
      setOrders(response.data.orders || response.data || []);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error(t('calendar.messages.loadOrdersError'));
    } finally {
      setLoading(false);
    }
  };

  // Get days in current month
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Add empty days for padding
    for (let i = 0; i < firstDay.getDay(); i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  }, [currentDate]);

  // Group orders by date
  const ordersByDate = useMemo(() => {
    const grouped = {};
    orders.forEach(order => {
      const dateKey = order.scheduled_date || order.created_at?.split('T')[0];
      if (dateKey) {
        if (!grouped[dateKey]) grouped[dateKey] = [];
        grouped[dateKey].push(order);
      }
    });
    return grouped;
  }, [orders]);

  // Get orders for selected date in list view
  const ordersForSelectedDate = useMemo(() => {
    return ordersByDate[selectedDate] || [];
  }, [ordersByDate, selectedDate]);

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };

  const selectedSpecialistData = specialists.find(s => s.id === selectedSpecialist);

  const dayHeaders = [
    t('calendar.dayHeaders.sun'),
    t('calendar.dayHeaders.mon'),
    t('calendar.dayHeaders.tue'),
    t('calendar.dayHeaders.wed'),
    t('calendar.dayHeaders.thu'),
    t('calendar.dayHeaders.fri'),
    t('calendar.dayHeaders.sat')
  ];

  return (
    <div className="space-y-6" data-testid="specialist-calendar-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('calendar.title')}</h1>
        <p className="text-gray-500 mt-1">{t('calendar.subtitle')}</p>
      </div>

      {/* Specialist Selector */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex-1 min-w-[250px]">
              <Select value={selectedSpecialist} onValueChange={setSelectedSpecialist}>
                <SelectTrigger data-testid="specialist-selector">
                  <SelectValue placeholder={t('calendar.selectSpecialist')} />
                </SelectTrigger>
                <SelectContent>
                  {specialists.map(spec => (
                    <SelectItem key={spec.id} value={spec.id}>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {spec.first_name} {spec.last_name} - {spec.specialist_type}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {selectedSpecialistData && (
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Phone className="h-4 w-4" />
                  {selectedSpecialistData.phone_country_code || '+57'} {selectedSpecialistData.phone}
                </span>
                <Badge variant="outline">{selectedSpecialistData.status}</Badge>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Calendar/List View */}
      {selectedSpecialist ? (
        <Tabs value={view} onValueChange={setView}>
          <div className="flex items-center justify-between mb-4">
            <TabsList>
              <TabsTrigger value="calendar" data-testid="view-calendar">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {t('calendar.calendarView')}
              </TabsTrigger>
              <TabsTrigger value="list" data-testid="view-list">
                <List className="h-4 w-4 mr-2" />
                {t('calendar.listView')}
              </TabsTrigger>
            </TabsList>
            
            {view === 'calendar' && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigateMonth(-1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="font-medium min-w-[150px] text-center">
                  {currentDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                </span>
                <Button variant="outline" size="sm" onClick={() => navigateMonth(1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            
            {view === 'list' && (
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-auto"
              />
            )}
          </div>

          <TabsContent value="calendar">
            <Card>
              <CardContent className="pt-6">
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
                  </div>
                ) : (
                  <div className="grid grid-cols-7 gap-1">
                    {/* Day headers */}
                    {dayHeaders.map(day => (
                      <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                        {day}
                      </div>
                    ))}
                    
                    {/* Calendar days */}
                    {daysInMonth.map((day, idx) => {
                      if (!day) {
                        return <div key={`empty-${idx}`} className="min-h-[80px]" />;
                      }
                      
                      const dateKey = day.toISOString().split('T')[0];
                      const dayOrders = ordersByDate[dateKey] || [];
                      const isToday = dateKey === new Date().toISOString().split('T')[0];
                      
                      return (
                        <div 
                          key={dateKey}
                          className={`min-h-[80px] border rounded-lg p-1 ${isToday ? 'bg-[#FDE8EA] border-[#E63946]' : 'border-gray-200'}`}
                        >
                          <div className={`text-xs font-medium mb-1 ${isToday ? 'text-[#E63946]' : 'text-gray-600'}`}>
                            {day.getDate()}
                          </div>
                          <div className="space-y-1">
                            {dayOrders.slice(0, 3).map(order => (
                              <div 
                                key={order.id} 
                                className={`text-xs px-1 py-0.5 rounded truncate ${priorityColors[order.priority] || 'bg-gray-200'}`}
                                title={order.patient_name || t('calendar.patientUnassigned')}
                              >
                                {order.patient_name?.split(' ')[0] || 'Order'}
                              </div>
                            ))}
                            {dayOrders.length > 3 && (
                              <div className="text-xs text-gray-500">+{dayOrders.length - 3} {t('calendar.more')}</div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="list">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {t('calendar.ordersFor')} {new Date(selectedDate + 'T12:00:00').toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
                </CardTitle>
                <CardDescription>
                  {ordersForSelectedDate.length} {t('calendar.ordersFound')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#E63946]" />
                  </div>
                ) : ordersForSelectedDate.length > 0 ? (
                  <div className="space-y-3">
                    {ordersForSelectedDate.map(order => (
                      <div key={order.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-[#FDE8EA] flex items-center justify-center">
                              <FileText className="h-5 w-5 text-[#E63946]" />
                            </div>
                            <div>
                              <p className="font-medium">{order.patient_name || t('calendar.patientUnassigned')}</p>
                              <p className="text-sm text-gray-500">
                                {order.diagnosis || order.services?.[0]?.name || t('calendar.noDiagnosis')}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={priorityColors[order.priority]}>
                              {order.priority}
                            </Badge>
                            <Badge className={statusColors[order.status]}>
                              {t(`status.${order.status}`)}
                            </Badge>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t grid grid-cols-2 gap-2 text-sm text-gray-600">
                          {order.service_address && (
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3 w-3" />
                              {order.service_address}
                            </span>
                          )}
                          {order.scheduled_time && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {order.scheduled_time}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <CalendarIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>{t('calendar.noOrdersForDate')}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center text-gray-500">
              <User className="h-16 w-16 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">{t('calendar.selectPrompt')}</p>
              <p className="text-sm">{t('calendar.selectPromptSubtitle')}</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Stats */}
      {selectedSpecialist && orders.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#E63946]">{orders.length}</p>
                <p className="text-sm text-gray-500">{t('calendar.stats.totalOrders')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {orders.filter(o => o.status === 'completed').length}
                </p>
                <p className="text-sm text-gray-500">{t('calendar.stats.completed')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-600">
                  {orders.filter(o => o.status === 'in_progress').length}
                </p>
                <p className="text-sm text-gray-500">{t('calendar.stats.inProgress')}</p>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {orders.filter(o => o.status === 'received' || o.status === 'confirmed').length}
                </p>
                <p className="text-sm text-gray-500">{t('calendar.stats.pending')}</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default SpecialistCalendarPage;
